import { SITE_URL, COMPANY, FAQ, ZONES, CONCIERGERIE_FAQ, MENAGE_FAQ, RATING, REVIEWS } from "./site";

const breadcrumb = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

const service = (name, description, path) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: name,
  name,
  description,
  url: `${SITE_URL}${path}`,
  provider: {
    "@type": "LocalBusiness",
    name: COMPANY.name,
    telephone: `+${COMPANY.phoneIntl.replace("+", "")}`,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Béziers",
      addressRegion: "Hérault",
      addressCountry: "FR",
    },
  },
  areaServed: ZONES.map((z) => z.name),
});
// eslint-disable-next-line no-unused-vars
const _serviceHelper = service;

export const SEO = {
  home: {
    title: "Conciergerie Airbnb & Ménage à Béziers | Cleanova-Sud",
    description:
      "Conciergerie Airbnb et ménage professionnel à Béziers et sur le littoral héraultais. Gestion de locations saisonnières, linge, préparation voyageurs et paniers d'accueil. Devis gratuit sous 24h.",
    path: "/",
    keywords:
      "conciergerie Airbnb Béziers, ménage location saisonnière, conciergerie Cap d'Agde, gestion Airbnb Hérault, nettoyage Airbnb",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Cleanova-Sud",
        url: SITE_URL,
      },
      {
        "@context": "https://schema.org",
        "@type": ["LocalBusiness", "CleaningService"],
        name: COMPANY.name,
        description:
          "Conciergerie Airbnb et ménage professionnel pour locations saisonnières à Béziers et sur le littoral héraultais.",
        url: SITE_URL,
        telephone: `+${COMPANY.phoneIntl.replace("+", "")}`,
        image: `${SITE_URL}/og-image.jpg`,
        priceRange: "€€",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Béziers",
          addressRegion: "Hérault",
          postalCode: "34500",
          addressCountry: "FR",
        },
        areaServed: ZONES.map((z) => ({ "@type": "City", name: z.name })),
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: RATING.value,
          reviewCount: String(RATING.count),
        },
        review: REVIEWS.map((r) => ({
          "@type": "Review",
          author: { "@type": "Person", name: r.name },
          reviewRating: {
            "@type": "Rating",
            ratingValue: "5",
            bestRating: "5",
          },
          reviewBody: r.text,
        })),
      },
      breadcrumb([{ name: "Accueil", path: "/" }]),
    ],
  },
  menage: {
    title: "Ménage Airbnb à Béziers | Nettoyage location saisonnière | Cleanova-Sud",
    description:
      "Ménage professionnel pour locations Airbnb à Béziers et sur le littoral héraultais : nettoyage complet, gestion du linge, lits faits et contrôle qualité entre chaque séjour. À partir de 1,30 €/m² — devis gratuit sous 24h.",
    path: "/menage",
    keywords:
      "ménage Airbnb Béziers, nettoyage location saisonnière, ménage Cap d'Agde, gestion du linge, ménage professionnel Hérault, nettoyage Airbnb Valras",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Ménage location saisonnière",
        name: "Ménage professionnel pour locations Airbnb à Béziers",
        description:
          "Nettoyage complet, gestion du linge, lits faits et contrôle qualité entre chaque séjour pour vos locations saisonnières à Béziers et sur le littoral héraultais.",
        url: `${SITE_URL}/menage`,
        provider: {
          "@type": "LocalBusiness",
          name: COMPANY.name,
          telephone: `+${COMPANY.phoneIntl.replace("+", "")}`,
          address: {
            "@type": "PostalAddress",
            addressLocality: "Béziers",
            addressRegion: "Hérault",
            postalCode: "34500",
            addressCountry: "FR",
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: RATING.value,
            reviewCount: String(RATING.count),
          },
        },
        areaServed: ZONES.map((z) => ({ "@type": "City", name: z.name })),
        offers: {
          "@type": "Offer",
          priceCurrency: "EUR",
          price: "1.30",
          description: "Ménage à partir de 1,30 €/m²",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: MENAGE_FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      breadcrumb([
        { name: "Accueil", path: "/" },
        { name: "Ménage", path: "/menage" },
      ]),
    ],
  },
  conciergerie: {
    title: "Conciergerie Airbnb à Béziers | Gestion location saisonnière | Cleanova-Sud",
    description:
      "Conciergerie Airbnb à Béziers et sur le littoral héraultais : accueil des voyageurs, remise des clés, ménage, gestion du linge et suivi propriétaire. Gestion clé en main de votre location saisonnière — devis gratuit sous 24h.",
    path: "/conciergerie",
    keywords:
      "conciergerie Airbnb Béziers, conciergerie Cap d'Agde, gestion location saisonnière Béziers, conciergerie Valras-Plage, gestion Airbnb Hérault, accueil voyageurs, gestion des clés",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Conciergerie Airbnb",
        name: "Conciergerie Airbnb à Béziers",
        description:
          "Service de conciergerie Airbnb à Béziers : accueil des voyageurs, remise des clés, ménage, gestion du linge, préparation du logement et suivi propriétaire pour vos locations saisonnières.",
        url: `${SITE_URL}/conciergerie`,
        provider: {
          "@type": "LocalBusiness",
          name: COMPANY.name,
          telephone: `+${COMPANY.phoneIntl.replace("+", "")}`,
          address: {
            "@type": "PostalAddress",
            addressLocality: "Béziers",
            addressRegion: "Hérault",
            postalCode: "34500",
            addressCountry: "FR",
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: RATING.value,
            reviewCount: String(RATING.count),
          },
        },
        areaServed: ZONES.map((z) => ({ "@type": "City", name: z.name })),
        offers: {
          "@type": "Offer",
          priceCurrency: "EUR",
          description: "Conciergerie facturée à 20% par nuitée",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: CONCIERGERIE_FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      breadcrumb([
        { name: "Accueil", path: "/" },
        { name: "Conciergerie", path: "/conciergerie" },
      ]),
    ],
  },
  apropos: {
    title: "À propos | Conciergerie & ménage Airbnb à Béziers | Cleanova-Sud",
    description:
      "Découvrez Groupe Cleanova - Sud, conciergerie spécialisée dans la gestion et l'entretien des locations saisonnières à Béziers et sur le littoral héraultais.",
    path: "/a-propos",
    keywords: "Cleanova-Sud, conciergerie Béziers, valeurs, équipe",
    jsonLd: breadcrumb([
      { name: "Accueil", path: "/" },
      { name: "À propos", path: "/a-propos" },
    ]),
  },
  faq: {
    title: "FAQ Conciergerie & Ménage Airbnb | Cleanova-Sud Béziers",
    description:
      "Toutes les réponses sur la conciergerie Airbnb à Béziers, le ménage pour locations saisonnières, la gestion du linge, les paniers d'accueil et l'application Cleanéo.",
    path: "/a-propos/faq-generale",
    keywords: "FAQ conciergerie Airbnb, questions ménage location saisonnière",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      breadcrumb([
        { name: "Accueil", path: "/" },
        { name: "À propos", path: "/a-propos" },
        { name: "FAQ", path: "/a-propos/faq-generale" },
      ]),
    ],
  },
  contact: {
    title: "Contact Conciergerie Airbnb Béziers | Devis gratuit | Cleanova-Sud",
    description:
      "Contactez Cleanova-Sud pour la gestion et l'entretien de vos locations Airbnb à Béziers et sur le littoral héraultais. Devis rapide et réponse sous 24h.",
    path: "/contact",
    keywords: "contact conciergerie Béziers, devis ménage Airbnb",
    jsonLd: breadcrumb([
      { name: "Accueil", path: "/" },
      { name: "Contact", path: "/contact" },
    ]),
  },
  services: {
    title: "Nos services : conciergerie, ménage, application & boutique | Cleanova-Sud",
    description:
      "Tous les services Cleanova-Sud : ménage professionnel, conciergerie Airbnb, application Cleanéo et paniers d'accueil pour vos locations saisonnières à Béziers.",
    path: "/nos-services",
    keywords: "services conciergerie Airbnb, ménage, application Cleanéo, paniers d'accueil",
    jsonLd: breadcrumb([
      { name: "Accueil", path: "/" },
      { name: "Nos services", path: "/nos-services" },
    ]),
  },
  applications: {
    title: "Cleanéo : application de gestion pour conciergeries & ménage | Cleanova-Sud",
    description:
      "Cleanéo, l'application tout-en-un pour gérer vos locations saisonnières : planning des interventions, suivi des équipes, logements et livret d'accueil digital.",
    path: "/nos-services/applications",
    keywords: "Cleanéo, application conciergerie, gestion ménage, livret d'accueil digital",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Cleanéo",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        description:
          "Application de gestion pour entreprises de ménage et conciergeries : planning, suivi des interventions et livret d'accueil digital.",
      },
      breadcrumb([
        { name: "Accueil", path: "/" },
        { name: "Nos services", path: "/nos-services" },
        { name: "Applications", path: "/nos-services/applications" },
      ]),
    ],
  },
  boutique: {
    title: "Paniers d'accueil pour locations Airbnb | Boutique Cleanova-Sud",
    description:
      "Offrez un accueil gourmand à vos voyageurs avec nos paniers d'accueil artisanaux (classique, gourmand, luxe) livrés directement dans vos locations saisonnières.",
    path: "/nos-services/boutique",
    keywords: "paniers d'accueil Airbnb, panier de bienvenue, accueil voyageurs",
    jsonLd: breadcrumb([
      { name: "Accueil", path: "/" },
      { name: "Nos services", path: "/nos-services" },
      { name: "Boutique", path: "/nos-services/boutique" },
    ]),
  },
};
